import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";
import jsPDF from "jspdf";
import HAFHAttendanceCapture from "./HAFHAttendanceCapture";

const capturePoints = [
  "main_gate",
  "mess",
  "room",
  "indoor_activity",
  "outdoor_activity",
  "offsite",
  "transit",
];

const HAFHAttendanceDisplay = ({ student, t, role }) => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();
  const [showCaptureAttendnace, setshowCaptureAttendnace] = useState(false);
  const [activeCaptureId, setActiveCaptureId] = useState(null);

  const userId = authState?.user?._id;
  const userName = authState?.user?.name;
  const userCode = authState?.user?.think5whyscode;

  const [attendance, setAttendance] = useState([]);

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const attendanceActions = ["IN", "OUT"];
  const [selectedActions, setSelectedActions] = useState([
    "IN",
    "OUT",
  ]);

  const lastFetchedFilterRef = useRef(null);

  const [selectedPoints, setSelectedPoints] = useState(capturePoints);

  const togglePoint = (point) => {
    setSelectedPoints((prev) =>
      prev.includes(point)
        ? prev.filter((p) => p !== point)
        : [...prev, point]
    );
  };

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const currentFilters = {
    fromDate,
    toDate,
    selectedPoints,
    selectedActions,
  };

  const handleShowAttendance = async () => {
    const filterKey = JSON.stringify(currentFilters);
    if (lastFetchedFilterRef.current === filterKey) {
      return;
    }
    lastFetchedFilterRef.current = filterKey;
    await fetchAttendance(currentFilters);
  };

  const getMode = (a) => {
    if (!a.in_time) return "IN";
    if (!a.out_time) return "OUT";
    return "UPDATE";
  };

  const [captureRecord, setCaptureRecord] = useState(null);
  const [captureMode, setCaptureMode] = useState("IN");

  const fetchAttendance = async (filter) => {
    try {
      const config = {
        url: "/hafh/core/attendance/my",
        method: "post", 
        data: {
          userId,
          fromDate: fromDate,
          toDate: toDate,
        },
        headers: {
          Authorization: authState.token,
        },
      };
      const response = await fetchData(config, {
        showSuccessToast: false,
      });
      if (response?.status) {
        setAttendance(response.records || []);
      }
    } catch (err) {
      console.error("Attendance fetch error:", err);
    }
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!userId) return;
    if (hasFetched.current) return;

    hasFetched.current = true;
    fetchAttendance({
      fromDate: today,
      toDate: today,
    });
  }, [userId]);

  /* ================= FILTER ================= */
  // const filteredAttendance = attendance;

  const filteredAttendance = useMemo(() => {
    return [...attendance]
      .filter((a) => {
        return (
          selectedPoints.length === 0 ||
          selectedPoints.includes(a.capturePoint)
        );
      })
      .sort((a, b) => {
        if (a.dateAttendance !== b.dateAttendance) {
          return b.dateAttendance.localeCompare(a.dateAttendance);
        }
        if (a.capturePoint !== b.capturePoint) {
          return a.capturePoint.localeCompare(b.capturePoint);
        }
        return (b.in_time || "00:00").localeCompare(
          a.in_time || "00:00"
        );
      })
  }, [attendance, selectedPoints]);

  /* ================= SHARE ================= */
  const handleShare = async () => {
    const shareText = filteredAttendance
      .map(
        (a) =>
          `${a.dateAttendance} | ${a.capturePoint} | IN: ${a.in_time || "--"} | OUT: ${a.out_time || "--"}`
      )
      .join("\n");

    const content = `
      HAFH Attendance Report

      Student: ${userName}

      From: ${fromDate}
      To: ${toDate}

      ${shareText}
      `;

    if (navigator.share) {
      await navigator.share({
        title: "HAFH Attendance",
        text: content,
      });
    } else {
      await navigator.clipboard.writeText(content);
      alert("Copied to clipboard");
    }
  };

  /* ================= PDF ================= */
  const handleDownloadPDF = () => {
    const pdf = new jsPDF();
    const now = new Date().toISOString().replace(/[:.]/g, "-");

    const fileName =
      `Think5Whys_HAFH_Attendance_${userName}_${userCode}_${fromDate}_to_${toDate}_${now}.pdf`;

    let y = 20;

    pdf.text("HAFH Attendance Report", 14, y);
    y += 10;

    pdf.text(`Name: ${userName || "Unknown"}`, 14, y);
    y += 10;

    filteredAttendance.forEach((a, i) => {
      if (y > 270) {
        pdf.addPage();
        y = 20;
      }

      pdf.text(
        `${i + 1}. ${a.dateAttendance} - ${a.capturePoint}`,
        14,
        y
      );
      y += 6;

      pdf.text(
        `IN: ${a.in_time || "--"} | OUT: ${a.out_time || "--"}`,
        14,
        y
      );
      y += 10;
    });

    pdf.save(fileName);
  };

  const openLocation = (lat, lng) => {
    if (!lat || !lng) {
      alert("Location not available");
      return;
    }

    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank");
  };

  const openRoute = (lat1, lng1, lat2, lng2) => {
    const url = `https://www.google.com/maps/dir/${lat1},${lng1}/${lat2},${lng2}`;
    window.open(url, "_blank");
  };

  const toggleAction = (action) => {
    setSelectedActions((prev) =>
      prev.includes(action)
        ? prev.filter((a) => a !== action)
        : [...prev, action]
    );
  };

  const isDateInvalid = !fromDate ||  !toDate ||  toDate < fromDate;

  return (
    <div className="bg-gray-800 p-4 rounded-2xl text-white space-y-5">
      <div className="flex justify-between">
        <h2 className="text-cyan-200 font-semibold text-sm lg:text-lg py-3">
          {t("attendanceReports")} <span className="px-2 text-gray-200">{filteredAttendance.length}</span>{t("records")} 
        </h2>

        <div className="text-sm text-gray-100">
          <button
            onClick={() => setshowCaptureAttendnace((prev) => !prev)}
            className={`px-1 py-2 rounded-md text-xs lg:text-sm w-48 lg:w-56 transition-all ${
              showCaptureAttendnace ? "bg-teal-600 text-white" : "bg-blue-700 text-white"
            }`}
          >
            {showCaptureAttendnace ? t("hideattendcapt") : t("showattendcapt") }
          </button> 
        </div>
      </div>
      {showCaptureAttendnace && (
        <HAFHAttendanceCapture
          student={captureRecord}
          t= {t}
          mode={captureMode}
          role={role}
          onClose={() => setCaptureRecord(null)}
        />
      )}

      <div className="bg-gray-900 p-3 rounded-2xl space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <input type="date" value={fromDate} className="bg-gray-700 text-white p-2 rounded border border-gray-600" onChange={(e) => setFromDate(e.target.value)} />
          <input type="date" value={toDate} className="bg-gray-700 text-white p-2 rounded border border-gray-600" onChange={(e) => setToDate(e.target.value)} />
          <button
            onClick={handleShowAttendance}
            disabled={isDateInvalid}
            className={`px-4 py-2 rounded text-xs lg:text-sm w-24 lg:w-32 transition-all ${
              isDateInvalid
                ? "bg-gray-600 cursor-not-allowed opacity-50"
                : "bg-teal-600 hover:bg-teal-500"
            }`}
          >
            Show
          </button>
        </div>
      </div>

      {/* ADVANCED FILTER TOGGLE */}
      <div className="flex justify-between items-center">
        <div className="text-xs lg:text-sm text-gray-300">
          Advanced Filters
        </div>

        <button
          onClick={() =>
            setShowAdvancedFilters((prev) => !prev)
          }
          className="text-xs lg:text-sm w-32 lg:w-40 px-3 py-2 rounded bg-gray-700 hover:bg-gray-600"
        >
          {showAdvancedFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* ADVANCED FILTERS */}
      {showAdvancedFilters && (
        <div className="space-y-3 border border-gray-700 rounded-xl p-3 bg-gray-800">
          {/* CAPTURE POINT FILTER */}
          <div>
            <div className="text-xs text-gray-400 mb-2">
              Capture Locations
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {capturePoints.map((p) => (
                <button
                  key={p}
                  onClick={() => togglePoint(p)}
                  className={`px-2 py-2 rounded-md text-xs lg:text-sm transition-all ${
                    selectedPoints.includes(p)
                      ? "bg-teal-600"
                      : "bg-gray-700"
                  }`}
                >
                  {t(p)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filteredAttendance.map((a, i) => (
          <div
            key={a._id || i}
            className="bg-gray-900 p-3 rounded-xl space-y-3"
          >
            <div className="flex items-center gap-2">
              <div className="text-xs lg:text-sm text-gray-200">
                {a.dateAttendance}
              </div>
              <div className="text-cyan-300 text-xs lg:text-sm">
                {t(a.capturePoint)}
              </div>
            </div>

            {/* IN ROW */}
            <div className="grid grid-cols-3 lg:grid-cols-3 items-center gap-1">
              <div
                className={`text-xs lg:text-sm ${
                  !a.in_time ? "text-orange-400" : "text-green-400"
                }`}
              >
                IN: {a.in_time || "Not Captured"}
              </div>

              <div className="col-span-2 lg:col-span-2 flex justify-start lg:justify-end">
                {a.capture_lat_in && a.capture_long_in && (
                  <button
                    onClick={() =>
                      openLocation(a.capture_lat_in, a.capture_long_in)
                    }
                    className="text-xs lg:text-sm w-40 lg:w-48 px-1 py-2 bg-blue-600 rounded"
                  >
                    📍 Time-In Location Map 
                  </button>
                )}
              </div>
            </div>

            {/* OUT ROW */}
            <div className="grid grid-cols-3 lg:grid-cols-3 items-center gap-1 mt-2">
              <div
                className={`text-xs lg:text-sm ${
                  !a.out_time ? "text-red-400" : "text-green-400"
                }`}
              >
                OUT: {a.out_time || "Not Captured"}
              </div>

              <div className="col-span-2 lg:col-span-2 flex justify-start lg:justify-end">
                {a.capture_lat_out && a.capture_long_out && (
                  <button
                    onClick={() =>
                      openLocation(a.capture_lat_out, a.capture_long_out)
                    }
                    className="text-xs lg:text-sm w-40 lg:w-48 px-1 py-2 bg-blue-600 rounded"
                  >
                    📍 Time-Out Location Map
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() =>
                openRoute(
                  a.capture_lat_in,
                  a.capture_long_in,
                  a.capture_lat_out,
                  a.capture_long_out
                )
              }
              className="text-xs lg:text-sm w-36 px-2 py-2 bg-green-600 rounded"
            >
              View Path
            </button>

            <div className="flex flex-row flex-wrap gap-2">
              <button
                onClick={() => {
                  // setCaptureRecord(a);
                  setCaptureMode(getMode(a));
                  setActiveCaptureId((prev) => (prev === a._id ? null : a._id));
                }}
                className={`text-xs lg:text-sm w-40 lg:w-48 px-1 py-2 rounded ${
                  !a.in_time
                    ? "bg-teal-600"
                    : !a.out_time
                    ? "bg-red-600"
                    : "bg-blue-600"
                }`}
              >
                {!a.in_time
                  ? "Capture Attendance"
                  : !a.out_time
                  ? "Capture Out...."
                  : "Update Attendance"}
              </button>
            </div>
            {activeCaptureId === a._id &&!a.out_time && (
              <div className="mt-3">
                <HAFHAttendanceCapture
                  student={a}
                  t={t}
                  mode={captureMode}
                  isUpdateRecord={true}
                  isUpdateTimeOut={true}
                />
              </div>
            )}
            {activeCaptureId === a._id && a.out_time && (
              <div className="mt-3">
                <HAFHAttendanceCapture
                  student={a}
                  t={t}
                  mode={captureMode}
                  isUpdateRecord={true}
                  isUpdateTimeOut={false}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ACTIONS */}
      <div className="flex flex-row gap-2">
          <button
            onClick={handleShare}
            className="w-24 lg:w-36 bg-teal-600 hover:bg-teal-500 transition-all px-2 py-2 rounded-xl text-sm font-medium"
            >
            {t("share")}
          </button>

          <button
            onClick={handleDownloadPDF}
            className="w-32 lg:w-44 bg-cyan-700 hover:bg-cyan-600 transition-all px-2 py-2 rounded-xl text-sm font-medium"
            >
            {t("downloadPDF")}
          </button>
      </div>
    </div>
  );
};

export default HAFHAttendanceDisplay;